import { createApp, reactive } from 'petite-vue';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import AirDatepicker from 'air-datepicker';
import 'air-datepicker/air-datepicker.css';

gsap.registerPlugin(ScrollToPlugin);

export default () => {
  const form = reactive({
    captchaToken: '',
    activeGroup: 1,
    nextGroup() {
      this.activeGroup += 1;
    },
    prevGroup() {
      this.activeGroup -= 1;
    },
    showWarnToast: false,
    reportMissingInput() {
      this.showWarnToast = true;
      setTimeout(() => {
        this.showWarnToast = false;
      }, 3000);
    },
    checkValidity() {
      const formElement = document.querySelector('form');

      const validity = formElement.checkValidity();

      if (!validity) {
        formElement.reportValidity();
        this.reportMissingInput();
      }

      return validity;
    },
    submit() {
      const validity = this.checkValidity();

      const formElement = document.querySelector('form');

      if (validity) {
        window.hcaptcha
          .execute({ async: true })
          .then(({ response }) => {
            this.captchaToken = response;
            formElement.submit();
          })
          .catch((err) => {
            console.error(err);
          });
      }
    },
  });

  function FormGroup(props) {
    return {
      groupNumber: props.groupNumber,
      groupType: props.groupType,
      radioValue: '',
      datePicker: '',
      nextGroup() {
        // This if for normale radio buttons
        if (this.groupType === 'radio' && this.radioValue !== '') {
          form.nextGroup();
          gsap.to(window, { duration: 0.5, scrollTo: 0 });
        }

        if (this.groupType === 'radio' && this.radioValue === '') {
          form.reportMissingInput();
        }

        // This if for radio buttons with date picker
        if (this.groupType === 'radio-date' && this.radioValue === 'unknown-date') {
          form.nextGroup();
          gsap.to(window, { duration: 0.5, scrollTo: 0 });
          this.datePicker = '';
        }

        if (
          this.groupType === 'radio-date' &&
          this.radioValue === 'fixed-date' &&
          this.datePicker !== ''
        ) {
          form.nextGroup();
          gsap.to(window, { duration: 0.5, scrollTo: 0 });
        }

        if (
          this.groupType === 'radio-date' &&
          this.datePicker === '' &&
          (this.radioValue === 'fixed-date' || this.radioValue === '')
        ) {
          form.reportMissingInput();
        }
      },
      prevGroup() {
        form.prevGroup();

        gsap.to(window, { duration: 0.5, scrollTo: 0 });
      },
      radioButtonStyle(inputElement) {
        form.showWarnToast = false;

        const groupWrapper = this.$refs.groupReference.parentElement.parentElement;
        const radioButtonCheckerSelector = '.request-contact_radio-button-checker';

        groupWrapper.querySelectorAll('.request-contact_radio-button-field').forEach((element) => {
          element.classList.remove('is-active');
          element.querySelector(radioButtonCheckerSelector).classList.remove('is-active');
        });

        const parentWrapper = inputElement.parentElement;
        const checkboxIcon = parentWrapper.querySelector(radioButtonCheckerSelector);

        if (inputElement.checked) {
          parentWrapper.classList.add('is-active');
          checkboxIcon.classList.add('is-active');
        }
      },
      initDatePicker(inputElement) {
        const setDatePickerValue = (date) => {
          this.datePicker = date.toLocaleDateString('de-DE', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
        };

        new AirDatepicker(inputElement, {
          locale: {
            days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
            daysShort: ['Son', 'Mon', 'Die', 'Mit', 'Don', 'Fre', 'Sam'],
            daysMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
            months: [
              'Januar',
              'Februar',
              'März',
              'April',
              'Mai',
              'Juni',
              'Juli',
              'August',
              'September',
              'Oktober',
              'November',
              'Dezember',
            ],
            monthsShort: [
              'Jan',
              'Feb',
              'Mär',
              'Apr',
              'Mai',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Okt',
              'Nov',
              'Dez',
            ],
            today: 'Heute',
            clear: 'Löschen',
            dateFormat: 'dd.MM.yyyy',
            timeFormat: 'HH:mm',
            firstDay: 1,
          },
          inline: false,
          minDate: new Date(),
          onSelect({ date }) {
            setDatePickerValue(date);
          },
        });
      },
    };
  }

  createApp({
    FormGroup,
    form,
    userFoodSavedID: localStorage.getItem('userId'),
  }).mount('#request-form-component');

  const availabilityPicker = reactive({
    weekdays: [],
    weekdaysNotEmpty() {
      if (this.weekdays.length > 0) {
        return true;
      }
      return false;
    },
  });

  function WeekdaySelector(props) {
    return {
      selected: false,
      weekname: props.weekname,
      toggleSelected() {
        this.selected = !this.selected;

        if (this.selected) {
          availabilityPicker.weekdays.push({ weekname: this.weekname });
        } else {
          availabilityPicker.weekdays = availabilityPicker.weekdays.filter(
            (weekday) => weekday.weekname !== this.weekname
          );
        }
      },
    };
  }

  createApp({
    WeekdaySelector,
    availabilityPicker,
    selectedTimes: [],
    submit() {
      window.hcaptcha
        .execute({ async: true })
        .then(({ response }) => {
          this.captchaToken = response;
          form.submit();
        })
        .catch((err) => {
          console.error(err);
        });
    },
    redirectEmptyUser() {
      const id = this.getUserIDFromURL();

      if (!id) {
        window.location.href = '/anfrage';
      }
    },
    getUserIDFromURL() {
      const url = new URL(window.location.href);
      return url.searchParams.get('user-id');
    },
    getFormTypeFromURL() {
      const url = new URL(window.location.href);
      return url.searchParams.get('form-type');
    },
  }).mount('#request-availability-form-component');
};
